<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Navigation -->
    <nav class="bg-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <!-- Logo -->
          <div class="flex items-center">
            <Link :href="route('home')" class="flex items-center">
              <img class="h-8 w-auto" src="/images/logo.svg" alt="SONNENGLAS" />
              <span class="ml-3 text-sm font-bold text-gray-900 whitespace-nowrap">Partner Portal</span>
            </Link>
          </div>

          <!-- Navigation Links -->
          <div class="hidden md:flex items-center space-x-4">
            <Link
              :href="route('news')"
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                route().current('news*')
                  ? 'bg-sonnenglas-100 text-sonnenglas-700'
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Neuigkeiten') }}
            </Link>
            <div
              class="relative"
              ref="productsDropdownRef"
              @mouseenter="openProductsDropdown"
              @mouseleave="scheduleCloseProductsDropdown"
            >
              <Link
                :href="route('products')"
                :class="[
                  'inline-flex items-center px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                  route().current('products*')
                    ? 'bg-sonnenglas-100 text-sonnenglas-700'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
                @click="showProductsMenu = false"
              >
                {{ $t('Produkte') }}
                <svg
                  v-if="productCategories.length > 0"
                  class="ml-1 h-3 w-3 transition-transform"
                  :class="{ 'rotate-180': showProductsMenu }"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
              </Link>

              <transition name="dropdown">
                <div
                  v-if="showProductsMenu && productCategories.length > 0"
                  class="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 max-h-96 overflow-y-auto"
                  @mouseenter="cancelCloseProductsDropdown"
                  @mouseleave="scheduleCloseProductsDropdown"
                >
                  <a
                    v-for="category in productCategories"
                    :key="category.id"
                    :href="`${route('products')}#category-${category.slug}`"
                    class="block px-4 py-2 text-xs text-gray-700 hover:bg-sonnenglas-50 hover:text-sonnenglas-700"
                    @click="onCategoryLinkClick($event, category.slug)"
                  >
                    {{ category.name }}
                  </a>
                </div>
              </transition>
            </div>
            <Link 
              :href="route('cart')" 
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap relative',
                route().current('cart') 
                  ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Warenkorb') }}
              <span 
                v-if="cartStore.totalItems > 0"
                class="absolute -top-1 -right-1 bg-sonnenglas-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
              >
                {{ cartStore.totalItems }}
              </span>
            </Link>
            <Link 
              :href="route('ordersList')" 
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                route().current('ordersList') 
                  ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Meine Bestellungen') }}
            </Link>
            <Link 
              :href="route('files')" 
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                route().current('files*') 
                  ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Downloads') }}
            </Link>
            <Link 
              :href="route('contactForm')" 
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                route().current('contactForm') 
                  ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Kontakt') }}
            </Link>
            <Link 
              :href="route('customerAccount')" 
              :class="[
                'px-2 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                route().current('customerAccount') || route().current('userAccount')
                  ? 'bg-sonnenglas-100 text-sonnenglas-700' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              {{ $t('Mein Konto') }}
            </Link>
          </div>

          <!-- User Menu -->
          <div class="flex items-center space-x-4">
            <!-- Language Toggle -->
            <LanguageToggle />
            
            <!-- User Dropdown -->
            <div class="relative" v-if="$page.props.auth.user">
              <button 
                @click="showUserMenu = !showUserMenu"
                class="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
              >
                <span class="sr-only">{{ $t('Benutzermenü öffnen') }}</span>
                <div class="h-8 w-8 rounded-full bg-sonnenglas-600 flex items-center justify-center text-white font-medium">
                  {{ $page.props.auth.user.name?.charAt(0).toUpperCase() }}
                </div>
              </button>
              
              <transition name="dropdown">
                <div 
                  v-if="showUserMenu"
                  class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5"
                >
                  <Link 
                    :href="route('get.logout')"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {{ $t('Abmelden') }}
                  </Link>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="pt-16">
      <slot />
    </main>

    <!-- Flash Messages -->
    <FlashMessage />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { useCartStore } from '@/stores/cart'
import LanguageToggle from '@/components/LanguageToggle.vue'
import FlashMessage from '@/components/FlashMessage.vue'

interface Props {
  title?: string
}

interface NavProductCategory {
  id: number
  name: string
  slug: string
}

defineProps<Props>()

const cartStore = useCartStore()
const showUserMenu = ref(false)
const showProductsMenu = ref(false)
const productsDropdownRef = ref<HTMLElement | null>(null)
const page = usePage()

// Shared via HandleInertiaRequests::buildProductCategoriesForNav()
const productCategories = computed<NavProductCategory[]>(() => {
  const props = page.props as Record<string, unknown>
  const list = props.productCategories
  return Array.isArray(list) ? (list as NavProductCategory[]) : []
})

let productsMenuCloseTimer: ReturnType<typeof setTimeout> | null = null

const openProductsDropdown = (): void => {
  if (productCategories.value.length === 0) {
    return
  }
  cancelCloseProductsDropdown()
  showProductsMenu.value = true
}

const scheduleCloseProductsDropdown = (): void => {
  cancelCloseProductsDropdown()
  productsMenuCloseTimer = setTimeout(() => {
    showProductsMenu.value = false
  }, 150)
}

const cancelCloseProductsDropdown = (): void => {
  if (productsMenuCloseTimer !== null) {
    clearTimeout(productsMenuCloseTimer)
    productsMenuCloseTimer = null
  }
}

const closeUserMenu = (event: Event) => {
  if (!(event.target as Element).closest('.relative')) {
    showUserMenu.value = false
  }
}

const onCategoryLinkClick = (event: MouseEvent, slug: string): void => {
  showProductsMenu.value = false

  // Modifier-clicks (cmd, ctrl, middle button) keep default browser behavior.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
    return
  }

  // If we're already on /products, prefer in-place smooth scroll over a full
  // page reload that would re-fetch products and pricing.
  const target = document.getElementById(`category-${slug}`)
  if (target) {
    event.preventDefault()
    history.replaceState(null, '', `#category-${slug}`)
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const closeProductsMenuOnOutsideClick = (event: Event): void => {
  const target = event.target as Node | null
  if (productsDropdownRef.value && target && !productsDropdownRef.value.contains(target)) {
    showProductsMenu.value = false
  }
}

onMounted(() => {
  // Initialize cart store with shared pricing data if available
  const sharedProps = page.props as any

  if (sharedProps.cartPricingTiers && sharedProps.cartProductTierPricing) {
    // Get cart products from session (we need the full product objects)
    // For now, we'll only initialize if we have the data
    const pricingTiers = sharedProps.cartPricingTiers
    const productTierPricing = sharedProps.cartProductTierPricing

    if (pricingTiers.length > 0) {
      // Initialize with empty products array for now - cart quantities are persisted separately
      cartStore.initializeCart([], {}, pricingTiers, productTierPricing)
    }
  }

  document.addEventListener('click', closeUserMenu)
  document.addEventListener('click', closeProductsMenuOnOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', closeUserMenu)
  document.removeEventListener('click', closeProductsMenuOnOutsideClick)
  cancelCloseProductsDropdown()
})
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.2s ease;
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>